<template>
  <div
    class="relative h-8 select-none"
    :class="{
      'w-40': size === 'regular',
      'w-24': size === 'small',
      'w-16': size === 'x-small',
      'w-full': size === 'full',
    }"
  >
    <div
      class="border-solid border rounded-lg h-full flex items-center py-2"
      :class="{
        'cursor-pointer': !disabled,
        'cursor-default': disabled,
        'rounded-b-none': open,
        'bg-grey-800 text-grey-100 border-grey-700 w-full': isDark,
        'border-grey-200': !isDark,
      }"
      @click="toggle"
    >
      <span
        class="select-none pl-4 truncate pr-6"
        :class="{
          'text-grey-200': placeholderShown && !isDark,
          'text-white font-bold': isDark
        }"
        >{{ text }}</span
      >
    </div>
    <span class="absolute bottom-0 inline-flex items-center p-2 right-0 top-0 cursor-pointer pointer-events-none" :class="{ 'text-grey-200': !isDark, 'text-grey-500': isDark, 'border-l': divider }">
      <arrow-down
        class="h-4 w-4 fill-current"
        @click="toggle"
      ></arrow-down>
    </span>
    <transition name="fade" mode="out-in">
      <div
        v-if="open"
        v-on-click-outside="closeMenu"
        class="
          border-solid border-l border-r border-b
          rounded-lg rounded-t-none
          shadow-lg
          relative
          z-10
          w-full
          overflow-auto
          custom-scrollbars
        "
        :class="{
          'bg-grey-800': isDark,
          'bg-white': !isDark,
          'border-grey-200': !isDark,
          'border-grey-700': isDark,
          'max-h-full': compress,
        }"
        :style="{ maxHeight }"
      >
        <div
          v-if="placeholder"
          :class="dropdownClass({}, 0)"
          class="italic"
          @click="select({ value: null })"
        >
          {{ placeholder }}
        </div>
        <div
          v-for="(action, index) in actions"
          :key="index"
          :class="{
            'py-2 px-4 hover:bg-grey-900 text-grey-100 hover:text-white select-none':
              isDark,
            'px-4 hover:bg-grey-900 hover:text-white select-none': !isDark,
            'border-b border-grey-700': isDark && index === actions.length - 1,
            'border-b border-grey-200': isDark && index === actions.length - 1,
          }"
          @click="callAction(action.callback)"
        >
          {{ action.text }}
        </div>
        <template v-if="styled">
          <div
            v-for="(option, index) in formattedOptions"
            :key="option.text"
            :class="dropdownClass(option, index)"
            :style="getStyle(styled, option)"
            @click="select(option)"
          >
            {{ option.text }}
          </div>
        </template>
        <template v-else>
          <div
            v-for="(option, index) in formattedOptions"
            :key="option.value"
            :class="dropdownClass(option, index)"
            @click="select(option)"
          >
            {{ option.text }}
          </div>
        </template>
      </div>
    </transition>
  </div>
</template>

<script>
import ArrowDown from '@fortawesome/fontawesome-free/svgs/solid/angle-down.svg';
import directives from '../directives';
import mixins from '../mixins';

export default {
  name: 'GdDropdown',
  components: {
    ArrowDown,
  },
  directives,
  mixins: [mixins],
  props: {
    compress: {},
    value: {},
    placeholder: {},
    options: {},
    dark: {},
    size: { default: 'regular' },
    actions: {
      default: () => [],
    },
    isGrouped: null,
    styled: null,
    maxHeight: { default: '400px' },
    disabled: Boolean,
    divider: {type: Boolean, default: false}
  },
  data() {
    return { open: false };
  },
  computed: {
    isDark() {
      return this.hasState('dark');
    },
    placeholderShown() {
      if (!this.value) return true;
      const value = this.formattedOptions.find(
        (option) => option.value === this.value
      );
      if (!value) return true;
      return false;
    },
    text() {
      const placeholder = this.placeholder || '-';
      let value;
      if (this.styled) {
        value = this.formattedOptions.find(
          (option) => option.value.name === this.value
        );
      } else {
        value = this.formattedOptions.find(
          (option) => option.value === this.value
        );
      }

      return value ? value.text : placeholder;
    },
    formattedOptions() {
      if (!this.options) return [];
      if (this.options[0] && this.options[0].value) return this.options;
      return this.options.map((option) => ({ text: option, value: option }));
    },
  },
  methods: {
    getStyle(styled, option) {
      return { [styled]: option.value.name };
    },
    callAction(action) {
      this.open = false;
      action();
    },
    toggle() {
      this.open = this.disabled ? false : !this.open;
    },
    select(option) {
      if(option.value === '---') {
        return;
      }
      this.open = false;
      this.$emit('input', option.value);
      this.$emit('change', option.value);
      this.$emit('changeValue', option.value);
    },
    closeMenu() {
      this.open = false;
    },
    dropdownClass(option, index) {
      let classList = 'select-none ';
      if(option.value === '---') {
        return `${classList} border-b-1 border-solid border-grey-300 cursor-none separator`;
      }
      if (option.value === this.value) classList += 'font-bold ';
      if (index === this.formattedOptions.length - 1)
        classList += 'rounded-lg rounded-t-none ';
      if (this.isDark) {
        return `${classList}px-4 py-2 hover:bg-grey-900 text-grey-100 hover:text-white`;
      }
      return `${classList}px-4 py-2 hover:bg-grey-900 hover:text-white`;
    },
  },
};
</script>

<style>
.custom-max-h-64 {
  max-height: 16rem;
}
.cursor-none {
  cursor: none;
}
.border-b-1 {
  border-bottom-width: 1px;
}
.separator {
  margin: 0 10px;
}
</style>
